import {
  Boxes,
  BookOpen,
  FileInput,
  LayoutTemplate,
  Share2,
  BookOpenCheck,
  FileUp,
  PhoneCall,
  Trash2,
  Banknote,
  BarChart3,
  Bot,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarClock,
  ClipboardCheck,
  Contact2,
  ListMusic,
  Sheet,
  CalendarDays,
  Newspaper,
  Sparkles,
  HeartPulse,
  Link2,
  ListChecks,
  MessageSquareHeart,
  Plug,
  Copy,
  CreditCard,
  FileSignature,
  FileStack,
  FileText,
  FolderKanban,
  Home,
  Globe2,
  GitBranch,
  Inbox,
  KanbanSquare,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Mail,
  Megaphone,
  MessageCircle,
  Receipt,
  Repeat,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  TrendingUp,
  UserCog,
  UserPlus,
  Users,
  Users2,
  Wallet,
  Workflow,
} from 'lucide-react';
import { usePermission } from '@/hooks/use-permission';
import { useSession } from '@/features/auth/use-session';

export type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Hidden unless the user holds this permission. */
  permission?: string;
  /** Hidden unless the user holds at least one of these. */
  anyOf?: string[];
  end?: boolean;
  /** Shown only to operators of the DovoCRM platform. */
  platformOnly?: boolean;
};

export type NavSection = {
  /** Empty for the pinned group at the top, which has no heading. */
  label: string;
  /** Stands for the group when the sidebar is collapsed to a rail. */
  icon: typeof LayoutDashboard;
  items: NavItem[];
};

/**
 * What the dashboard and My day actually draw on. A role built for one job —
 * somebody who only enters leads — holds none of these, and for them the
 * two overview screens would be empty boxes with a menu pointing at them.
 */
const DASHBOARD_ANY = [
  'admin.user.view',
  'admin.role.view',
  'admin.team.view',
  'report.dashboard.view',
  'crm.deal.view',
  'finance.invoice.view',
  'project.project.view',
  'service.ticket.view',
];
const MY_DAY_ANY = ['crm.task.view', 'crm.deal.view', 'project.task.view', 'service.ticket.view'];

export const SECTIONS: NavSection[] = [
  {
    // The records everybody opens all day: no heading, never collapsed.
    label: '',
    icon: Home,
    items: [
      { to: '/dashboard', label: 'Home', icon: Home, end: true, anyOf: DASHBOARD_ANY },
      { to: '/my-day', label: 'My day', icon: CalendarCheck, anyOf: MY_DAY_ANY },
      { to: '/inbox', label: 'Inbox', icon: Inbox, permission: 'inbox.conversation.view', end: true },
      { to: '/crm/contacts', label: 'Contacts', icon: Contact2, permission: 'crm.contact.view' },
      { to: '/crm/companies', label: 'Companies', icon: Building2, permission: 'crm.company.view' },
      { to: '/crm/deals', label: 'Deals', icon: Briefcase, permission: 'crm.deal.view' },
      { to: '/crm/leads', label: 'Leads', icon: UserPlus, permission: 'crm.lead.view', end: true },
    ],
  },
  {
    label: 'CRM',
    icon: LayoutGrid,
    items: [
      { to: '/crm/leads/board', label: 'Lead pipeline', icon: LayoutGrid, permission: 'crm.lead.view' },
      { to: '/crm/quotes', label: 'Quotes', icon: FileSignature, permission: 'crm.quote.view' },
      { to: '/objects', label: 'Custom objects', icon: Boxes, permission: 'crm.object.view' },
      {
        to: '/crm/digital-pipeline/lead',
        label: 'Digital pipeline',
        icon: Workflow,
        permission: 'admin.workflow.view',
      },
      {
        to: '/crm/pipelines/deal',
        label: 'Deal pipeline setup',
        icon: KanbanSquare,
        permission: 'crm.pipeline.create',
      },
      {
        to: '/crm/pipelines/lead',
        label: 'Lead pipeline setup',
        icon: GitBranch,
        permission: 'crm.pipeline.create',
      },
      {
        to: '/sales/import',
        label: 'Import & export',
        icon: FileUp,
        anyOf: ['crm.import.view', 'crm.lead.export', 'crm.contact.export', 'crm.company.export', 'crm.deal.export'],
      },
      {
        to: '/crm/duplicates',
        label: 'Duplicates',
        icon: Copy,
        anyOf: ['crm.contact.update', 'crm.company.update'],
      },
      { to: '/crm/google-sheets', label: 'Google Sheets', icon: Sheet, permission: 'crm.import.view' },
      {
        to: '/sales/recycle-bin',
        label: 'Recycle bin',
        icon: Trash2,
        anyOf: ['crm.lead.delete', 'crm.contact.delete', 'crm.company.delete', 'crm.deal.delete'],
      },
    ],
  },
  {
    label: 'Sales',
    icon: Target,
    items: [
      { to: '/sales/sequences', label: 'Sequences', icon: Workflow, permission: 'crm.sequence.view' },
      { to: '/sales/meetings', label: 'Meetings', icon: CalendarClock, permission: 'crm.meeting.view' },
      { to: '/sales/emails', label: 'Emails & templates', icon: Mail, permission: 'crm.email.view' },
      { to: '/sales/calls', label: 'Calls', icon: PhoneCall, permission: 'crm.call.view' },
      { to: '/sales/coaching', label: 'Coaching playlists', icon: ListMusic, permission: 'crm.call.view' },
      { to: '/sales/playbooks', label: 'Playbooks', icon: BookOpenCheck, permission: 'crm.playbook.view' },
      { to: '/sales/mailbox', label: 'My email & calendar', icon: Inbox, permission: 'crm.email.send' },
      { to: '/sales/telephony', label: 'Phone system', icon: PhoneCall, permission: 'crm.telephony.view' },
      { to: '/sales/activity', label: 'Activity & goals', icon: Target, permission: 'crm.quota.view' },
      { to: '/task-queues', label: 'Task queues', icon: ListChecks, permission: 'crm.task.view' },
    ],
  },
  {
    label: 'Marketing',
    icon: Megaphone,
    items: [
      { to: '/marketing/campaigns', label: 'Email campaigns', icon: Mail, permission: 'marketing.campaign.view' },
      { to: '/marketing/segments', label: 'Segments', icon: Users2, permission: 'marketing.segment.view' },
      { to: '/marketing/journeys', label: 'Journeys', icon: GitBranch, permission: 'marketing.campaign.view' },
      {
        to: '/marketing/whatsapp',
        label: 'WhatsApp',
        icon: MessageCircle,
        permission: 'marketing.campaign.view',
      },
      { to: '/marketing/forms', label: 'Forms & pop-ups', icon: FileInput, permission: 'marketing.form.view' },
      { to: '/marketing/pages', label: 'Landing pages', icon: LayoutTemplate, permission: 'marketing.page.view' },
      { to: '/marketing/blog', label: 'Blog', icon: Newspaper, permission: 'marketing.blog.view' },
      { to: '/marketing/events', label: 'Events', icon: CalendarDays, permission: 'marketing.event.view' },
      { to: '/marketing/website', label: 'Website & tracking', icon: Globe2, permission: 'marketing.site.view' },
      { to: '/marketing/ads', label: 'Lead ads', icon: Megaphone, permission: 'marketing.ads.view' },
      { to: '/marketing/social', label: 'Social posts', icon: Share2, permission: 'marketing.social.view' },
      {
        to: '/marketing/settings',
        label: 'Marketing setup',
        icon: SlidersHorizontal,
        permission: 'marketing.campaign.view',
      },
    ],
  },
  {
    label: 'Conversations',
    icon: MessageCircle,
    items: [
      { to: '/chat', label: 'Team chat', icon: MessageCircle, permission: 'chat.channel.view' },
      { to: '/inbox/salesbots', label: 'Salesbots', icon: Bot, permission: 'inbox.salesbot.view' },
      { to: '/inbox/ai-agent', label: 'AI agent', icon: Sparkles, permission: 'inbox.channel.view' },
      { to: '/inbox/channels', label: 'Channels', icon: SlidersHorizontal, permission: 'inbox.channel.view' },
    ],
  },
  {
    label: 'Service',
    icon: LifeBuoy,
    items: [
      { to: '/service/tickets', label: 'Tickets', icon: LifeBuoy, permission: 'service.ticket.view' },
      { to: '/service/contracts', label: 'Contracts', icon: FileSignature, permission: 'service.contract.view' },
      { to: '/service/knowledge-base', label: 'Knowledge base', icon: BookOpen, permission: 'service.kb.view' },
      { to: '/service/surveys', label: 'Feedback surveys', icon: MessageSquareHeart, permission: 'service.survey.view' },
      { to: '/service/customer-success', label: 'Customer success', icon: HeartPulse, permission: 'crm.company.view' },
      {
        to: '/service/settings',
        label: 'Service setup',
        icon: CalendarClock,
        permission: 'service.sla.view',
      },
    ],
  },
  {
    label: 'Commerce',
    icon: Wallet,
    items: [
      { to: '/finance', label: 'Overview', icon: Wallet, permission: 'finance.invoice.view', end: true },
      { to: '/finance/invoices', label: 'Invoices', icon: Receipt, permission: 'finance.invoice.view' },
      { to: '/finance/payments', label: 'Payments', icon: Banknote, permission: 'finance.payment.view' },
      { to: '/finance/payment-links', label: 'Payment links', icon: Link2, permission: 'finance.paymentlink.view' },
      { to: '/finance/expenses', label: 'Expenses', icon: CreditCard, permission: 'finance.expense.view' },
      {
        to: '/finance/subscriptions',
        label: 'Subscriptions',
        icon: Repeat,
        permission: 'product.subscription.view',
      },
      { to: '/finance/reports', label: 'Finance reports', icon: BarChart3, permission: 'finance.ledger.view' },
      {
        to: '/finance/forecast',
        label: 'Cash forecast',
        icon: TrendingUp,
        permission: 'finance.forecast.view',
      },
      { to: '/finance/budgets', label: 'Budgets', icon: Target, permission: 'finance.budget.view' },
      {
        to: '/finance/settings',
        label: 'Finance setup',
        icon: SlidersHorizontal,
        permission: 'finance.settings.view',
      },
    ],
  },
  {
    label: 'Projects',
    icon: FolderKanban,
    items: [
      { to: '/delivery/projects', label: 'Projects', icon: FolderKanban, permission: 'project.project.view' },
      { to: '/delivery/board', label: 'Work board', icon: LayoutGrid, permission: 'project.task.view' },
      {
        to: '/delivery/timesheets',
        label: 'Timesheets',
        icon: ClipboardCheck,
        permission: 'project.timesheet.view',
      },
    ],
  },
  {
    label: 'Documents',
    icon: FileStack,
    items: [
      { to: '/documents', label: 'Vault', icon: FileStack, permission: 'file.document.view', end: true },
      {
        to: '/documents/templates',
        label: 'Templates & signing',
        icon: FileSignature,
        permission: 'file.document.view',
      },
    ],
  },
  {
    label: 'Reporting',
    icon: BarChart3,
    items: [
      { to: '/insights', label: 'Dashboards', icon: BarChart3, permission: 'report.dashboard.view' },
      { to: '/reports', label: 'Reports', icon: ClipboardCheck, permission: 'report.report.view' },
      { to: '/reports/sales-analytics', label: 'Sales analytics', icon: TrendingUp, permission: 'report.report.view' },
      { to: '/ai-analyst', label: 'AI analyst', icon: Sparkles, permission: 'report.report.view' },
    ],
  },
  {
    label: 'Automation',
    icon: Workflow,
    items: [{ to: '/admin/workflows', label: 'Workflows', icon: Bot, permission: 'admin.workflow.view' }],
  },
  {
    label: 'Settings',
    icon: Settings,
    items: [
      { to: '/admin/users', label: 'Users', icon: Users, permission: 'admin.user.view' },
      { to: '/admin/roles', label: 'Roles', icon: ShieldCheck, permission: 'admin.role.view' },
      { to: '/admin/teams', label: 'Teams', icon: UserCog, permission: 'admin.team.view' },
      {
        to: '/admin/organization',
        label: 'Organisation',
        icon: Settings,
        permission: 'admin.organization.view',
      },
      { to: '/settings/properties', label: 'Properties', icon: SlidersHorizontal, permission: 'admin.customfield.view' },
      { to: '/settings/lead-scoring', label: 'Lead scoring', icon: Target, permission: 'admin.customfield.update' },
      { to: '/admin/billing', label: 'Plan & billing', icon: CreditCard, permission: 'admin.organization.view' },
      { to: '/admin/integrations', label: 'Integrations', icon: Plug, permission: 'admin.integration.view' },
      { to: '/admin/audit', label: 'Audit log', icon: FileText, permission: 'admin.audit.view' },
      {
        to: '/admin/portal-users',
        label: 'Portal access',
        icon: ShieldCheck,
        permission: 'portal.user.view',
      },
      { to: '/platform', label: 'Platform console', icon: Globe2, platformOnly: true },
    ],
  },
];

/**
 * The order the phone's bottom bar fills its two "quick" slots in: the
 * screens somebody opens many times a day, most specific job first.
 */
const QUICK_PRIORITY = [
  '/inbox',
  '/crm/leads',
  '/crm/deals',
  '/service/tickets',
  '/delivery/board',
  '/finance/invoices',
  '/my-day',
  '/delivery/projects',
  '/marketing/campaigns',
  '/admin/users',
];

export function isVisible(
  item: NavItem,
  can: (key: string) => boolean,
  isPlatformAdmin = false,
): boolean {
  if (item.platformOnly && !isPlatformAdmin) return false;
  if (item.permission && !can(item.permission)) return false;
  if (item.anyOf && !item.anyOf.some(can)) return false;
  return true;
}

/** The sections this user can see, with the items they cannot use removed. */
export function useVisibleSections(): NavSection[] {
  const { can } = usePermission();
  const { data: user } = useSession();
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => isVisible(item, can, user?.isPlatformAdmin)),
  })).filter((section) => section.items.length > 0);
}

/**
 * Where "home" is for this user: the dashboard when they can use it, otherwise
 * the first screen they can — a lead-entry role lands on Leads, not on a
 * dashboard of things they cannot open.
 */
export function useHomeRoute(): string {
  const sections = useVisibleSections();
  const first = sections[0]?.items[0];
  return first?.to ?? '/account';
}

/** The phone's bottom bar: home, two quick screens, chat, and the full menu. */
export function useQuickItems(): NavItem[] {
  const sections = useVisibleSections();
  const items = sections.flatMap((section) => section.items);
  const byPath = new Map(items.map((item) => [item.to, item]));
  const quick = QUICK_PRIORITY.map((to) => byPath.get(to)).filter((item): item is NavItem => Boolean(item));
  return quick.slice(0, 2);
}

/** Whether the dashboard screen has anything to show this user. */
export function useCanSeeDashboard(): boolean {
  const { canAny } = usePermission();
  return canAny(...DASHBOARD_ANY);
}

export function useCanSeeMyDay(): boolean {
  const { canAny } = usePermission();
  return canAny(...MY_DAY_ANY);
}
